import { hasSensitiveTelemetryLocation } from "./telemetryPrivacy";

const PRIVATE_SESSION_KEY = "fairpath_private_telemetry_session";
const RETAINED_CREDENTIAL_KEYS = [
  "fairpath_guest_access_code",
  "fairpath_guest_legacy_url_token",
  "fairpath_guest_token",
  "fp_mail_code",
];

type PrivacyState = {
  private: boolean;
  observing: boolean;
  listeners: Set<() => void>;
};

const states = new WeakMap<Window, PrivacyState>();

function stateForWindow(): PrivacyState {
  let state = states.get(window);
  if (!state) {
    state = { private: false, observing: false, listeners: new Set() };
    states.set(window, state);
  }
  return state;
}

function retainPrivateState(state: PrivacyState): void {
  if (state.private) return;
  state.private = true;
  try {
    window.sessionStorage.setItem(PRIVATE_SESSION_KEY, "1");
  } catch {
    // The in-memory latch still protects navigation in this document.
  }
  for (const listener of state.listeners) {
    try { listener(); } catch { /* Analytics must not interrupt navigation. */ }
  }
}

/** Retain only a boolean; never copy a URL, code or credential into this flag. */
export function isPrivateTelemetrySession(
  url = typeof window === "undefined" ? "" : window.location.href,
  referrer = typeof document === "undefined" ? "" : document.referrer,
): boolean {
  if (typeof window === "undefined") return true;
  const state = stateForWindow();
  if (state.private) return true;

  let shouldRetain = hasSensitiveTelemetryLocation(url) ||
    hasSensitiveTelemetryLocation(referrer);
  try {
    shouldRetain ||= window.sessionStorage.getItem(PRIVATE_SESSION_KEY) === "1";
    shouldRetain ||= RETAINED_CREDENTIAL_KEYS.some((key) =>
      Boolean(window.sessionStorage.getItem(key) || window.localStorage.getItem(key)),
    );
  } catch {
    // We cannot establish whether this browser retained a private session.
    shouldRetain = true;
  }
  if (!shouldRetain) return false;

  retainPrivateState(state);
  return true;
}

export function onPrivateTelemetrySession(listener: () => void): void {
  if (typeof window === "undefined") return;
  stateForWindow().listeners.add(listener);
  if (isPrivateTelemetrySession()) {
    try { listener(); } catch { /* Analytics must not interrupt navigation. */ }
  }
}

/** Run before URL cleanup and deferred SDKs, including SPA history changes. */
export function observeTelemetrySessionPrivacy(): void {
  if (typeof window === "undefined") return;
  const state = stateForWindow();
  isPrivateTelemetrySession();
  if (state.observing) return;
  state.observing = true;

  try {
    for (const method of ["pushState", "replaceState"] as const) {
      const original = window.history[method];
      window.history[method] = function (...args: Parameters<History[typeof method]>) {
        if (args[2] != null) {
          try {
            isPrivateTelemetrySession(new URL(args[2], window.location.href).href);
          } catch {
            // Preserve the browser's original invalid-URL behavior below.
          }
        }
        return original.apply(this, args);
      };
    }
    window.addEventListener("popstate", () => isPrivateTelemetrySession(), true);
  } catch {
    // An environment that prevents observing navigation remains private.
    retainPrivateState(state);
  }
}
